import { z } from "zod";

import type { Draft } from "@/prisma/generated/client";
import type { Prisma } from "@/prisma/generated/client";

export const saveDraftBodyworkDataSchema = z.discriminatedUnion(
  "needsBodywork",
  [
    z.object({
      id: z.number().int().positive(),
      needsBodywork: z.literal(false),
    }),
    z.object({
      id: z.number().int().positive(),
      needsBodywork: z.literal(true),
      bodyTypeId: z.number().int().positive(),
      bodyShopKvps: z.string().trim().min(1).max(20),
      bodyShopAccountNumber: z.string().trim().min(1).max(20),
      bodyShopSiret: z.string().trim().min(1).max(14),
      bodyShopPostalCode: z.string().trim().min(1).max(10),
      shippedToBodyShop: z.boolean(),
    }),
  ],
);

export type SaveDraftBodyworkDataInput = z.infer<
  typeof saveDraftBodyworkDataSchema
>;

export type NeedsBodyworkAnswer = "" | "yes" | "no";

export type ShippedAnswer = "" | "yes" | "no";

export type BodyworkFormState = {
  needsBodyworkAnswer: NeedsBodyworkAnswer;
  bodyTypeId: string;
  bodyShopKvps: string;
  bodyShopAccountNumber: string;
  bodyShopSiret: string;
  bodyShopPostalCode: string;
  shippedAnswer: ShippedAnswer;
};

const EMPTY_BODYWORK_FORM_STATE: BodyworkFormState = {
  needsBodyworkAnswer: "",
  bodyTypeId: "",
  bodyShopKvps: "",
  bodyShopAccountNumber: "",
  bodyShopSiret: "",
  bodyShopPostalCode: "",
  shippedAnswer: "",
};

export type DraftBodyworkFields = Pick<
  Draft,
  | "needsBodywork"
  | "bodyTypeId"
  | "bodyShopKvps"
  | "bodyShopAccountNumber"
  | "bodyShopSiret"
  | "bodyShopPostalCode"
  | "shippedToBodyShop"
>;

export function mapDraftToBodyworkFormState(
  draft: DraftBodyworkFields | null | undefined,
): BodyworkFormState {
  if (draft === null || draft === undefined) {
    return EMPTY_BODYWORK_FORM_STATE;
  }

  if (draft.needsBodywork === null || draft.needsBodywork === undefined) {
    return EMPTY_BODYWORK_FORM_STATE;
  }

  if (!draft.needsBodywork) {
    return { ...EMPTY_BODYWORK_FORM_STATE, needsBodyworkAnswer: "no" };
  }

  return {
    needsBodyworkAnswer: "yes",
    bodyTypeId:
      draft.bodyTypeId !== null && draft.bodyTypeId !== undefined
        ? String(draft.bodyTypeId)
        : "",
    bodyShopKvps: draft.bodyShopKvps ?? "",
    bodyShopAccountNumber: draft.bodyShopAccountNumber ?? "",
    bodyShopSiret: draft.bodyShopSiret ?? "",
    bodyShopPostalCode: draft.bodyShopPostalCode ?? "",
    shippedAnswer:
      draft.shippedToBodyShop === null || draft.shippedToBodyShop === undefined
        ? ""
        : draft.shippedToBodyShop
          ? "yes"
          : "no",
  };
}

const BODYWORK_FIELDS_CLEARED: Prisma.DraftUpdateInput = {
  needsBodywork: false,
  bodyType: { disconnect: true },
  bodyShopKvps: null,
  bodyShopAccountNumber: null,
  bodyShopSiret: null,
  bodyShopPostalCode: null,
  shippedToBodyShop: null,
};

export function buildBodyworkDraftUpdate(
  payload: SaveDraftBodyworkDataInput,
): Prisma.DraftUpdateInput {
  if (!payload.needsBodywork) {
    return BODYWORK_FIELDS_CLEARED;
  }

  return {
    needsBodywork: true,
    bodyType: { connect: { id: payload.bodyTypeId } },
    bodyShopKvps: payload.bodyShopKvps,
    bodyShopAccountNumber: payload.bodyShopAccountNumber,
    bodyShopSiret: payload.bodyShopSiret,
    bodyShopPostalCode: payload.bodyShopPostalCode,
    shippedToBodyShop: payload.shippedToBodyShop,
  };
}
